import { z } from 'zod';
import type { QuoteCalcFormulaRow, QuoteCalcGroupKey, QuoteCalcGroupText, QuoteCalcStandardPosition } from './quote-calculation.schema';
import type { ScraperType } from './quote-calculation-engine';

/**
 * Übernahme der Vorlagen aus der base44-App „Vertrieb" (ADR 0112): Exporte
 * `Berechnung`, `Standardposition`, `StandardGruppenText`. Reine Abbildung —
 * der Server ersetzt damit die jeweilige Vorlagenliste.
 *
 * base44-Artikel-IDs lassen sich nicht auf den ProERP-Artikelstamm abbilden;
 * die Standardpositionen kommen daher ohne Artikel und werden in „Formeln &
 * Vorlagen" je Kategorie zugeordnet.
 */

const text = z.string().nullish().transform((value) => value ?? '');
const nullableText = z.string().nullish().transform((value) => (value?.trim() ? value : null));

export const base44FormulaRecordSchema = z.object({
  kategorie: z.string().trim().min(1),
  formel_4_wellen_raeumer: nullableText,
  formel_3_wellen_raeumer: nullableText,
  formel_2_wellen_boden: nullableText,
  formel_2_wellen_flotat: nullableText,
  spezifikation_de: text,
  spezifikation_en: text,
  attribut_de_1: text,
  attribut_de_2: text,
  attribut_en_1: text,
  attribut_en_2: text,
});

export const base44StandardPositionRecordSchema = z.object({
  gruppe: z.string().trim().min(1),
  kategorie: z.string().trim().min(1),
  reihenfolge: z.number().int().nullish(),
  show_in_offer: z.boolean().nullish(),
  show_in_specs: z.boolean().nullish(),
});

export const base44GroupTextRecordSchema = z.object({
  gruppe: z.string().trim().min(1),
  scraper_type: z.string().nullish(),
  pauschal: z.boolean().nullish(),
  title_de: text,
  title_en: text,
  zusatz_de: text,
  zusatz_en: text,
});

/** Request-Body des Imports: jede Liste optional; fehlende Listen bleiben unverändert. */
export const quoteCalcBase44ImportSchema = z.object({
  formulas: z.array(base44FormulaRecordSchema).max(500).optional(),
  standardPositions: z.array(base44StandardPositionRecordSchema).max(500).optional(),
  groupTexts: z.array(base44GroupTextRecordSchema).max(200).optional(),
});
export type QuoteCalcBase44Import = z.infer<typeof quoteCalcBase44ImportSchema>;

const GROUPS: Record<string, QuoteCalcGroupKey> = {
  kunststoff: 'PLASTIC',
  elektro: 'ELECTRICAL',
  stahl: 'STEEL',
  montage: 'ASSEMBLY',
  'dokumentation/engineering': 'DOCUMENTATION',
  dokumentation: 'DOCUMENTATION',
  fracht: 'FREIGHT',
  optionen: 'OPTIONS',
  option: 'OPTIONS',
};

const BASE44_SCRAPER_TYPES: Record<string, ScraperType> = {
  '4-shaft scraper': 'FOUR_SHAFT',
  '3-shaft scraper': 'THREE_SHAFT',
  '2-shaft bottom scraper': 'TWO_SHAFT_BOTTOM',
  '2-shaft surface scraper': 'TWO_SHAFT_SURFACE',
  div: 'OTHER',
};

/** base44-Räumertyp („4-shaft scraper", „Div") → ScraperType; unbekannt = undefined. */
export function base44ScraperType(name: string | null | undefined): ScraperType | undefined {
  return BASE44_SCRAPER_TYPES[(name ?? "").trim().toLowerCase()];
}

export function base44GroupKey(name: string): QuoteCalcGroupKey | null {
  return GROUPS[name.trim().toLowerCase()] ?? null;
}

export interface Base44TemplateMapping {
  formulas?: QuoteCalcFormulaRow[];
  standardPositions?: QuoteCalcStandardPosition[];
  groupTexts?: QuoteCalcGroupText[];
  /** Was nicht übernommen werden konnte, mit Grund. */
  skipped: string[];
}

export function mapBase44Templates(input: QuoteCalcBase44Import): Base44TemplateMapping {
  const skipped: string[] = [];
  const result: Base44TemplateMapping = { skipped };

  if (input.formulas) {
    const seen = new Set<string>();
    result.formulas = [];
    input.formulas.forEach((record, index) => {
      if (seen.has(record.kategorie)) {
        skipped.push(`Formel „${record.kategorie}": doppelt, nur die erste übernommen`);
        return;
      }
      seen.add(record.kategorie);
      result.formulas!.push({
        category: record.kategorie,
        formulas: {
          FOUR_SHAFT: record.formel_4_wellen_raeumer,
          THREE_SHAFT: record.formel_3_wellen_raeumer,
          TWO_SHAFT_BOTTOM: record.formel_2_wellen_boden,
          TWO_SHAFT_SURFACE: record.formel_2_wellen_flotat,
        },
        specTitle: { de: record.spezifikation_de, en: record.spezifikation_en },
        specLabels: {
          de: [record.attribut_de_1, record.attribut_de_2].filter(Boolean),
          en: [record.attribut_en_1, record.attribut_en_2].filter(Boolean),
        },
        sortOrder: index,
      });
    });
  }

  if (input.standardPositions) {
    result.standardPositions = [];
    for (const record of input.standardPositions) {
      const group = base44GroupKey(record.gruppe);
      if (!group) {
        skipped.push(`Standardposition „${record.kategorie}": unbekannte Gruppe „${record.gruppe}"`);
        continue;
      }
      result.standardPositions.push({
        group,
        category: record.kategorie,
        articleId: null,
        sortOrder: record.reihenfolge ?? 0,
        showInOffer: record.show_in_offer ?? true,
        showInSpecs: record.show_in_specs ?? false,
      });
    }
  }

  if (input.groupTexts) {
    result.groupTexts = [];
    for (const record of input.groupTexts) {
      const group = base44GroupKey(record.gruppe);
      if (!group) {
        skipped.push(`Gruppentext „${record.title_de}": unbekannte Gruppe „${record.gruppe}"`);
        continue;
      }
      const typeName = record.scraper_type?.trim().toLowerCase() ?? '';
      const scraperType = typeName ? BASE44_SCRAPER_TYPES[typeName] : null;
      if (scraperType === undefined) {
        skipped.push(`Gruppentext „${record.title_de}": unbekannter Räumertyp „${record.scraper_type}"`);
        continue;
      }
      result.groupTexts.push({
        group,
        scraperType,
        isLumpSum: record.pauschal ?? false,
        title: { de: record.title_de, en: record.title_en },
        text: { de: record.zusatz_de, en: record.zusatz_en },
      });
    }
  }

  return result;
}
